import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMcpBridge } from '../../mcp/McpBridgeProvider';
import { fetchPortalInstancesPage, type PortalInstance } from '../../portal/portalClient';
import { instanceQueryKey } from './useInstanceLabel';
import { usePortalAccess } from './usePortalAccess';

const STALE_TIME = 5 * 60 * 1000;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** ARTESCA instance ids are UUIDs; SFDC install-base ids are 15/18-char alphanumerics. */
export const isArtescaInstanceId = (value: string): boolean => UUID_PATTERN.test(value.trim());

/**
 * Translate whatever the MCP host handed us into an SFDC install-base id.
 *
 * Which id arrives depends on the host, and both branches below are load-bearing:
 *
 * - **ARTESCA UI** (`source=artesca_ui`) answers `getInstanceId` with its own **ARTESCA
 *   instance UUID**, which guardian cannot use directly — the portal's `artescaInstanceId`
 *   filter is what maps it onto an install-base record.
 * - **RING and ADI** answer `getInstanceId` with the **SFDC install-base id** already, so
 *   there is nothing to translate and the id is confirmed via `?id=` instead.
 *
 * The branch is chosen by the id's *shape*, not by the host: the bridge records what
 * `getInstanceId` returned, never who returned it, and the portal answers a non-UUID
 * `artescaInstanceId` with a 400, so trying one and falling back is not available. Shape is
 * also the more forgiving test — the two namespaces cannot collide (an SFDC id is 15/18
 * base62 characters and never hyphenated), and a host that changed which id it reports
 * would still be routed correctly, where a `source` check would silently misroute it.
 *
 * So the `?id=` branch is NOT dead code left over from the removed `xxx-` workaround: it is
 * how RING and ADI resolve at all.
 */
export const useResolvedMcpInstanceId = () => {
  const { mcpInstanceId } = useMcpBridge();
  const { baseUrl, token, canQuery } = usePortalAccess();
  const queryClient = useQueryClient();

  const candidate = mcpInstanceId?.trim() ?? '';
  const byArtescaId = !!candidate && isArtescaInstanceId(candidate);
  const isEnabled = canQuery && !!candidate;

  const { data, isPending, isFetching } = useQuery<PortalInstance | null>({
    queryKey: ['portal-mcp-instance', baseUrl, candidate],
    queryFn: async () => {
      const page = await fetchPortalInstancesPage({
        baseUrl,
        token,
        ...(byArtescaId ? { artescaInstanceIds: [candidate] } : { ids: [candidate] }),
      });

      const matches = page.instances.filter((instance): instance is PortalInstance & { id: string } => !!instance.id);
      if (matches.length > 1 && process.env.NODE_ENV === 'development') {
        // Every match is an ARTESCA record by construction, so there is no better
        // discriminator available client-side.
        console.warn(
          `[guardian] ${matches.length} portal instances matched ${candidate}; using the first (${matches[0].id}).`,
        );
      }

      const match = matches[0];
      if (!match) return null;

      // Prime the by-id cache: the label lookup that follows the id into the URL would
      // otherwise re-fetch the record we are already holding, flashing the raw id first.
      queryClient.setQueryData(instanceQueryKey(baseUrl, match.id), match);
      return match;
    },
    enabled: isEnabled,
    staleTime: STALE_TIME,
    retry: false,
  });

  return {
    resolvedId: data?.id ?? null,
    isResolving: isEnabled && (isPending || isFetching),
  };
};
